import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CameraDetail,
  DeviceStorageListResponse,
  TimelineEvents,
  VideoList,
} from "../../../../api/types/CamerasAdminVM";
import {
  dbTimeToJsTime,
  formattedDateTimeBasedOnTimezone,
  jsTimeToDBTime,
} from "../../../../utils/dateTime";
import {
  convertToTZForDateFnsNoHook,
  useAccountTimezone,
} from "../../../../hooks/timezone";
import { APP_CONFIG } from "../../../../constants/constants";
import {
  AdminFormFieldDropdown,
  AdminFormFieldInput,
  AdminFormFieldSubmit,
} from "../../../../components/admin/formFields";
import {
  downloadEventVideo,
  useEventGetVideoDetailsMutation,
  useGetEventVideoDownloadUrlMutation,
  useGetTaskStatusByIdQuery,
  useLazyGetDeviceGeneralInfoQuery,
} from "../../../../api/network/vmApiService";
import {
  CameraDeviceConfig,
  CameraEventGetVideoDetailResponse,
  CameraWakeupRequiredInfo,
  CameraTaskProcessResponse,
  DeviceEvent,
} from "../../../../api/types/Camera";
import { useDispatch, useSelector } from "react-redux";
import { setModalsData, TModalsState } from "../../../../api/store/commonSlice";
import ModalDownloadConfirmation from "../modalDownloadConfirmation";
import svgIcons from "../../../../assets/svg/svgIcons";
import { useRequestVideoMutation } from "../../../../api/network/camerasAdminVMApiService";
import { toast } from "react-toastify";
import ModalWakeupCamera from "../../management/modalWakeupCamera";
import {
  CAMERA_CAPTURE_MODES,
  CAMERA_ID_TYPES,
  ALL_CAMERA_CLIP_TYPES,
  CONNECTIVITY_STATE,
  RECORDING_TYPE,
} from "../../constants";
import ModalVMVideoPlayback from "../../components/modalVMVideoPlayback";
import StorageVideoView from "../../components/storageVideoView";
import AppVideoElement from "../../components/videoElement";
import { fromZonedTime } from "date-fns-tz";

const OPTION_VIDEO_CLIP = [
  { label: "30 sec", value: "30" },
  { label: "60 sec", value: "60" },
  { label: "90 sec", value: "90" },
  { label: "120 sec", value: "120" },
  { label: "180 sec", value: "180" },
];

enum VIDEO_PLAYBACK_STATE {
  playing = "playing",
  paused = "paused",
  not_started = "not_started",
}

interface EventDetailsProps {
  eventList: DeviceEvent[];
  selectedEvent?: DeviceEvent | null;
  selectedTimelineData?: VideoList[] | null;
  onEventSelect?: (event: DeviceEvent) => void;
  selectedTime: number;
  serial_num: string;
  cameraId: string;
  config: CameraDeviceConfig | null;
  cameraDetail: CameraDetail | null;
  captureMode?: string | undefined | null;
  dataDeviceStorage?: DeviceStorageListResponse[] | null;
  onCustomTimeSelected?: (time: number) => void;
}

const EventDetails: FC<EventDetailsProps> = ({
  eventList,
  selectedEvent,
  selectedTimelineData,
  onEventSelect,
  serial_num,
  cameraId,
  selectedTime,
  config,
  cameraDetail,
  captureMode,
  dataDeviceStorage,
  onCustomTimeSelected,
}) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "camera.dashboard.video_history.video_playback",
  });
  const dispatch = useDispatch();
  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );
  const { convertToTZForDateFns, tzForDateFns } = useAccountTimezone();
  const [eventVideoDetails, setEventVideoDetails] =
    useState<CameraEventGetVideoDetailResponse | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [downloadingVideo, setDownloadingVideo] = useState(false);
  const [playRequested, setPlayRequested] = useState(false);
  // const [taskId, setTaskId] = useState<string | null>(); // Task Id
  // const [startTaskUpdates, setStartTaskUpdates] = useState<boolean>(false); // Flag to trigger task updates on task creation
  const [clipDuration, setClipDuration] = useState(30); // Duration of clip to be downloaded or played for storage
  const [clipType, setClipType] = useState(ALL_CAMERA_CLIP_TYPES[0].value); // Clip type of camera to be requested (Default: ALL)
  const [selectedClipTypes, setSelectedClipTypes] = useState<string[]>([
    CAMERA_CAPTURE_MODES.front,
  ]);
  const [clipOptions, setClipOptions] = useState([ALL_CAMERA_CLIP_TYPES[0]]); // Options for clip type selection
  const [timelineVideoDetails, setTimelineVideoDetails] =
    useState<CameraTaskProcessResponse | null>(null);
  const [currentCaptureMode, setCurrentCaptureMode] = useState<string | null>(null);
  const currentTimestampRef = useRef<number>(0);
  const remainingSecondsRef = useRef<number>(0);
  const switchInFlightRef = useRef(false);
  const [playbackState, setPlaybackState] = useState<VIDEO_PLAYBACK_STATE>(
    VIDEO_PLAYBACK_STATE.not_started
  );
  const requestVideoAction = useRef<"play" | "download">();

  const isSDCardDataSelected = selectedTimelineData !== null ? true : false;

  const [
    eventGetVideoDetailsAPITrigger,
    {
      isLoading: isLoadinVideoDetails,
      isUninitialized: isUninitializedVideoDetails,
    },
  ] = useEventGetVideoDetailsMutation();
  // const [downloadEventVideoApiTrigger, { isLoading: isDownloadingEventVideo }] =
  //   useGetEventVideoDownloadUrlMutation();

  const [generalInfoApiTrigger, { isLoading: isLoadingCameraInfo }] =
    useLazyGetDeviceGeneralInfoQuery();

  /**
   *
   */
  // const { currentData: taskStatusResponse, isLoading: isLoadingTaskStatus } =
  //   useGetTaskStatusByIdQuery(taskId ?? "", {
  //     skip: !taskId || !startTaskUpdates,
  //     pollingInterval: 1 * 1000,
  //     skipPollingIfUnfocused: true,
  //   });

  // let taskStatusData = taskStatusResponse?.data ?? null;

  /**
   * Method to reset all timline video task related flags
   */
  const resetTask = useCallback((clearData = false) => {
    // setTaskId(null);
    setDownloadingVideo(false);
    setPlayRequested(false);
    // setStartTaskUpdates(false);
    setPlaybackState(VIDEO_PLAYBACK_STATE.not_started);
    if (clearData) {
      setEventVideoDetails(null);
      setTimelineVideoDetails(null);
    }
    dispatch(setModalsData({ ...modalsState, showModalVideoPlayback: null }));
  }, [modalsState]);

  // Toggle play/pause and manage playback state
  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    if (video.paused) {
      video.play();
      setPlaybackState(VIDEO_PLAYBACK_STATE.playing);
    } else {
      video.pause();
      setPlaybackState(VIDEO_PLAYBACK_STATE.paused);
    }
  }, [videoRef]);

  const handlePlay = () => setPlaybackState(VIDEO_PLAYBACK_STATE.playing);
  const handlePause = () => setPlaybackState(VIDEO_PLAYBACK_STATE.paused);
  const handleEnded = () => setPlaybackState(VIDEO_PLAYBACK_STATE.not_started);

  // Update playback state on video events
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    video.addEventListener("play", handlePlay);
    video.addEventListener("pause", handlePause);
    video.addEventListener("ended", handleEnded);

    return () => {
      video.removeEventListener("play", handlePlay);
      video.removeEventListener("pause", handlePause);
      video.removeEventListener("ended", handleEnded);
    };
  }, [videoRef]);

  /**
   * Effect to reset any running task process on new event or video event selection
   */
  useEffect(() => {
    resetTask(true);
    if (selectedTimelineData && selectedTimelineData?.length > 0) {
      setClipType(
        selectedTimelineData?.[0].captureMode ?? CAMERA_CAPTURE_MODES.extended
      );
      const clipTypeOption = ALL_CAMERA_CLIP_TYPES.filter((option) => {
        const clipOption = selectedTimelineData?.find(
          (item) => item.captureMode === option.value
        );
        if (!!clipOption) return true;
        return false;
      });
      const validOptions =
        clipTypeOption.length > 0 ? clipTypeOption : [ALL_CAMERA_CLIP_TYPES[0]];
      setClipOptions(validOptions);

      // Initialize selectedClipTypes with first valid option
      if (validOptions.length > 0) {
        setSelectedClipTypes([validOptions[0].value]);
      }
    }
  }, [selectedEvent, selectedTimelineData]);

  /**
   * Effect to fetch event video details using its ticket id
   */
  useEffect(() => {
    if (!!selectedEvent?.ticket) {
      setEventVideoDetails(null);
      eventGetVideoDetailsAPITrigger({
        ticketId: selectedEvent?.ticket,
      })
        .unwrap()
        .then((res) => {
          if (!!res.data) setEventVideoDetails(res.data);
          else {
            if (APP_CONFIG.DEBUG.ALL)
              console.log("Failed to retrieve event video details");
          }
        })
        .catch((e) => {
          if (APP_CONFIG.DEBUG.ALL)
            console.log("Error while retrieving event video details");
        });
    }
  }, [selectedEvent, eventGetVideoDetailsAPITrigger]);

  const onDownloadEventVideoClick = () => {
    if (selectedEvent && selectedEvent?.ticket) {
      dispatch(
        setModalsData({ ...modalsState, showEventDownloadConfirmation: true })
      );
    }
  };

  const onRequestStorageVideoClick = () => {
    if (selectedClipTypes.length === 0) {
      toast.error("Please select at least one camera type.");
      return;
    }

    if (selectedClipTypes.length > 4) {
      toast.warning("You can select maximum of 4 camera type.");
      return;
    }
    // Request video related data from server
    setDownloadingVideo(true);
    requestVideoAction.current = "download"; // Set action to download
    triggerVideoRequestProcess();
  };

  /**
   * Method to download event related video
   */
  const handleEventVideoDownload = useCallback(() => {
    // Download the video file using the URL and Name available in event details
    downloadEventVideo(
      eventVideoDetails?.videos[0]?.video_url ?? "",
      eventVideoDetails?.videos[0]?.origin_file_name ?? ""
    );
    /**
     * Below code can be used if whole event data(Screenshot, video, other data) needs to be downloaded in one pacakge
     */
    // const fileName = eventVideoDetails?.title?.split("_")[0] || "";
    // setDownloadingVideo(true);
    // downloadEventVideoApiTrigger({
    //   ticketId: eventVideoDetails?.ticket ?? "",
    //   fileName,
    // })
    //   .then((res) => {
    //     setDownloadingVideo(false);
    //     if (res?.data?.download_url) {
    //       // Download the video file using the URL
    //       downloadEventVideo(res.data.download_url, fileName);
    //     } else {
    //       console.error("Failed to retrieve video download URL");
    //     }
    //   })
    //   .catch((error) => {
    //     setDownloadingVideo(false);
    //     console.error("Error downloading video:", error);
    //   });
  }, [eventVideoDetails]);

  const [requestVideoApiTrigger] = useRequestVideoMutation();

  const onRequestVideo = useCallback(async () => {
    if (!selectedTimelineData) {
      return;
    }

    if (requestVideoAction.current === "download") {
      toast.info(
        `Requesting ${selectedClipTypes.length} video(s) from the device...`
      );

      let successCount = 0;
      let failCount = 0;

      for (const type of selectedClipTypes) {
        try {
          await requestVideoApiTrigger({
            timestamp: selectedTimelineData?.[0].time ?? 0,
            serial_number: serial_num,
            duration_in_seconds: clipDuration,
            cam_id: type,
          }).unwrap();

          successCount++;
          await new Promise((resolve) => setTimeout(resolve, 500));
        } catch (error) {
          console.error(`Error requesting view ${type}:`, error);
          failCount++;
        }
      }

      if (successCount > 0) {
        toast.success(
          `${successCount} file(s) requested successfully. Check File Retrievals.`
        );
      }
      if (failCount > 0) {
        toast.error(`${failCount} request(s) failed.`);
      }
      setDownloadingVideo(false);
    } else if (requestVideoAction.current === "play") {
      playSDCardVideo();
    }
  }, [
    selectedTimelineData,
    downloadingVideo,
    playRequested,
    requestVideoAction,
    requestVideoApiTrigger,
    serial_num,
    clipDuration,
    clipType,
    selectedClipTypes,
  ]);

  const onTimeUpdate = useCallback((currentTimestamp: number, remainingSeconds: number) => {
    currentTimestampRef.current = currentTimestamp;
    remainingSecondsRef.current = remainingSeconds;
  }, []);

  const nextCameraLabel = useMemo(() => {
    if (selectedClipTypes.length < 2) return "";
    const currentIndex = currentCaptureMode !== null ? selectedClipTypes.indexOf(currentCaptureMode) : -1;
    const nextValue = currentIndex === -1
      ? selectedClipTypes[0]
      : selectedClipTypes[(currentIndex + 1) % selectedClipTypes.length];
    return ALL_CAMERA_CLIP_TYPES.find((o) => o.value === nextValue)?.label ?? nextValue;
  }, [selectedClipTypes, currentCaptureMode]);

  // Clear the re-entrancy guard once Redux reflects the switch we just dispatched,
  // so a genuine follow-up click isn't blocked by a stale in-flight flag.
  useEffect(() => {
    switchInFlightRef.current = false;
  }, [modalsState.showModalVideoPlayback?.captureMode]);

  const onNextCamera = useCallback(() => {
    if (selectedClipTypes.length < 2 || switchInFlightRef.current) return;
    switchInFlightRef.current = true;
    const currentIndex = currentCaptureMode !== null ? selectedClipTypes.indexOf(currentCaptureMode) : -1;
    const nextMode = currentIndex === -1
      ? selectedClipTypes[0]
      : selectedClipTypes[(currentIndex + 1) % selectedClipTypes.length];
    const nextStorage = dataDeviceStorage?.find(d => d.capture_mode === nextMode)?.storage_type;
    setCurrentCaptureMode(nextMode);
    dispatch(
      setModalsData({
        ...modalsState,
        showModalVideoPlayback: {
          startTimestamp: currentTimestampRef.current,
          duration: remainingSecondsRef.current,
          captureMode: nextMode,
          storageType: nextStorage,
          action: "play",
        },
      })
    );
  }, [currentCaptureMode, selectedClipTypes, dataDeviceStorage, modalsState, dispatch]);

  const playSDCardVideo = () => {
    setCurrentCaptureMode(selectedClipTypes[0]);
    // Enable webRTC video playback
    const storageDevice = dataDeviceStorage?.find(d => d.capture_mode === selectedClipTypes[0])?.storage_type;
    // Seed refs so a Next click before the first onTimeUpdate tick doesn't dispatch a stale 0/0 timestamp
    currentTimestampRef.current = selectedTimelineData?.[0]?.time ?? 0;
    remainingSecondsRef.current = clipDuration;
    dispatch(
      setModalsData({
        ...modalsState,
        showModalVideoPlayback: {
          startTimestamp: selectedTimelineData?.[0]?.time ?? 0,
          duration: clipDuration,
          captureMode: selectedClipTypes[0],
          storageType: storageDevice,
          action: "play", // Indicate action for playback
        },
      })
    );
  };

  const onDeviceWakeupSuccess = (serial_num: string) => {
    // Device is awake, request video from camera
    onRequestVideo();
  };

  const onDeviceWakeupFailure = (error: string) => {
    // Unable to wakeup the device, abort processes
    resetTask();
  };

  const onCancelWakeup = () => {
    // Unable to wakeup the device, abort processes
    resetTask(true);
  };

  /**
   * Calculates the duration of the video in seconds (or formatted as HH:MM:SS)
   */
  // const videoDuration = useMemo(() => {
  //   if (
  //     (!selectedEvent ||
  //       !eventVideoDetails?.video_start_timestamp ||
  //       !eventVideoDetails?.video_end_timestamp) &&
  //     !selectedTimelineData
  //   ) {
  //     return "";
  //   }
  //   let durationSec = 0;
  //   if (selectedEvent && eventVideoDetails) {
  //     durationSec = eventVideoDetails?.duration
  //       ? Number(eventVideoDetails?.duration?.toFixed(2))
  //       : 0;
  //   } else if (selectedTimelineData) {
  //     durationSec = selectedTimelineData.duration;
  //   }
  //   if (durationSec <= 0) return "0:00";
  //   // Return duration in seconds only
  //   return `${durationSec.toString()} sec`;
  // }, [eventVideoDetails, selectedEvent, selectedTimelineData]);

  const eventDate = useMemo(() => {
    if (selectedEvent && eventVideoDetails) {
      return formattedDateTimeBasedOnTimezone?.(
        dbTimeToJsTime(selectedEvent?.timestamp ?? 0),
        convertToTZForDateFnsNoHook(tzForDateFns),
        APP_CONFIG.DATETIME_FORMATS.DISPLAY.DATE_TIME
      );
    } else if (selectedTimelineData && selectedTime) {
      return formattedDateTimeBasedOnTimezone?.(
        selectedTime,
        "",
        APP_CONFIG.DATETIME_FORMATS.DISPLAY.DATE_TIME
      );
    } else {
      return "-";
    }
  }, [eventVideoDetails, selectedEvent, selectedTimelineData, tzForDateFns]);

  const eventTime = useMemo(() => {
    if (selectedTime) {
      return formattedDateTimeBasedOnTimezone?.(
        selectedTime,
        "",
        APP_CONFIG.DATETIME_FORMATS.DISPLAY.TIME_ONLY
      );
    } else {
      return "";
    }
  }, [selectedTime]);

  /**
   * Method to trigger request video process by first checking if device is online or offline.
   * If device is offline,  then wakeup device
   * If device is online, then proceed with request
   */
  const triggerVideoRequestProcess = () => {
    generalInfoApiTrigger(cameraId)
      .then((res) => {
        if (res && res?.error) {
          console.error("Failed to fetch device info:", res.error);
          toast.error("Unable to reach device. Please try again.");
          resetTask();
          return;
        }

        if (res && res?.data) {
          const dataDeviceInfo = res?.data;
          const infoData = dataDeviceInfo?.data[0];
          if (infoData.connectivity_state === CONNECTIVITY_STATE.online) {
            onRequestVideo();
          } else if (
            infoData.connectivity_state === CONNECTIVITY_STATE.offline ||
            infoData.connectivity_state === CONNECTIVITY_STATE.standby
          ) {
            // Device is offline, request wakeup
            dispatch(
              setModalsData({
                ...modalsState,
                showModalWakeupCamera: {
                  cid: cameraId, // infoData.cid,
                  serial_number: serial_num, // infoData.serial_number,
                  onSuccessCallback: onDeviceWakeupSuccess,
                  onFailureCallback: onDeviceWakeupFailure,
                  onCancelCallback: onCancelWakeup,
                } as CameraWakeupRequiredInfo,
              })
            );
          } else {
            // Device state is unknown, cancel any play or download request
            resetTask();
          }
        }
      })
      .catch((error) => {
        // Unable to get device info, cancel any play or download request
        resetTask();
      });
  };

  /**
   * Method to handle play button click
   */
  const onPlayVideoClick = () => {
    if (isSDCardDataSelected) {
      if (playbackState === VIDEO_PLAYBACK_STATE.playing) {
        dispatch(
          setModalsData({
            ...modalsState,
            showModalVideoPlayback: {
              // Preserve the live playhead (matches onNextCamera) so a later
              // playClip-driven resume/reconnect continues from here, not the timeline mark.
              startTimestamp: currentTimestampRef.current,
              duration: remainingSecondsRef.current,
              captureMode: modalsState.showModalVideoPlayback?.captureMode ?? clipType,
              storageType: modalsState.showModalVideoPlayback?.storageType ?? "SDCARD",
              action: "pause", // Indicate action for playback
            },
          })
        );
      } else if (playbackState === VIDEO_PLAYBACK_STATE.paused) {
        dispatch(
          setModalsData({
            ...modalsState,
            showModalVideoPlayback: {
              // Preserve the live playhead (matches onNextCamera) so a later
              // playClip-driven resume/reconnect continues from here, not the timeline mark.
              startTimestamp: currentTimestampRef.current,
              duration: remainingSecondsRef.current,
              captureMode: modalsState.showModalVideoPlayback?.captureMode ?? clipType,
              storageType: modalsState.showModalVideoPlayback?.storageType ?? "SDCARD",
              action: "play", // Indicate action for playback
            },
          })
        );
      } else {
        setPlayRequested(true); // Enable flag to indicate play is requested
        requestVideoAction.current = "play"; // Set action to play
        triggerVideoRequestProcess();
      }
    } else {
      togglePlay();
    }
  };

  const videoUrl = useMemo(() => {
    if (selectedEvent && eventVideoDetails) {
      return eventVideoDetails?.videos?.[0]?.video_url;
    } else if (
      selectedTimelineData &&
      timelineVideoDetails?.videos &&
      timelineVideoDetails?.videos?.length > 0
    ) {
      return timelineVideoDetails?.videos?.[0]?.video_url;
    }
    return "";
  }, [
    eventVideoDetails,
    selectedEvent,
    selectedTimelineData,
    timelineVideoDetails?.videos,
  ]);

  const playButtonTitle = useMemo(() => {
    if (videoUrl) {
      if (playbackState === VIDEO_PLAYBACK_STATE.playing) {
        return "Pause";
      }
      return "Play";
    }
    return "Play";
  }, [videoUrl, playbackState]);

  const snapshotUrl = useMemo(() => {
    if (selectedEvent && eventVideoDetails) {
      return eventVideoDetails?.snapshots?.[0]?.url;
    } else if (
      selectedTimelineData &&
      timelineVideoDetails?.videos &&
      timelineVideoDetails?.videos?.length > 0
    ) {
      return timelineVideoDetails?.videos[0]?.screenshot_url;
    }
    return "";
  }, [
    eventVideoDetails,
    selectedEvent,
    selectedTimelineData,
    timelineVideoDetails,
  ]);

  const onStorageVideoPlayStart = (response: any) => {
    setPlayRequested(false);
    setPlaybackState(VIDEO_PLAYBACK_STATE.playing);
  };
  const onStorageVideoPaused = () => {
    console.log("onStorageVideoPaused");
    setPlaybackState(VIDEO_PLAYBACK_STATE.paused);
  };

  const onStorageVideoStopped = () => {
    setPlayRequested(false);
    setPlaybackState(VIDEO_PLAYBACK_STATE.not_started);
  };

  const onDatetimePickerChange = (fieldName: string, value: any) => {
    if (APP_CONFIG.DEBUG.REPORTS) console.log("==", fieldName, value);
    const currentSelectedDate = formattedDateTimeBasedOnTimezone(
      selectedTime,
      null,
      "yyyy-MM-dd"
    );
    const selectedDate = new Date(`${currentSelectedDate}T${value}`);
    // const utcDate = fromZonedTime(
    //   selectedDate,
    //   convertToTZForDateFnsNoHook(tzForDateFns)
    // );
    if (APP_CONFIG.DEBUG.REPORTS)
      console.log("timestamp", jsTimeToDBTime(selectedDate.getTime()));

    onCustomTimeSelected?.(selectedDate.getTime());
    // setFieldValue(fieldName, jsTime / 1000);
    // if (fieldName === "start_time") {
    //   // prefill end time with start time + 1 day
    //   const endDate = new Date(jsTime);
    //   endDate.setDate(endDate.getDate() + 1);
    //   setFieldValue(
    //     "end_time",
    //     endDate
    //       ? datepickerValueToUTC(
    //           `${endDate.toISOString().split("T")[0]}T23:59:59`,
    //           values.timezone
    //         )
    //       : ""
    //   );
    // }
  };

  return (
    <>
      {((selectedEvent && eventVideoDetails) || selectedTimelineData) && (
        <div className="col-span-9 grid grid-cols-12 gap-2 bg-white p-4 rounded-lg">
          <div className="col-span-2 flex items-center font-medium text-xs leading-6 tracking-normal">
            <span className="max-w-20">{eventDate}</span>
          </div>
          <div className="col-span-4">
            <AdminFormFieldInput
              label={t("timeline")}
              type="time-picker"
              id="timeline"
              name="timeline"
              value={formattedDateTimeBasedOnTimezone(
                selectedTime,
                null,
                APP_CONFIG.DATETIME_FORMATS.DISPLAY.DATE_TIME
              )}
              onDatepickerChange={onDatetimePickerChange}
            />
          </div>
          {/* <div className="col-span-3 flex flex-col justify-between py-2">
            <div className="font-semibold text-sm leading-3 tracking-normal">
              {t("video_length")}
            </div>
            <div className="font-medium text-xs leading-6 tracking-normal">
              {`${videoDuration}`}
            </div>
          </div>
          */}
          {selectedEvent && (
            <div className="col-span-6 flex gap-1 items-center mt-5">
              <AdminFormFieldSubmit
                label={
                  playRequested ? (
                    <img
                      src={svgIcons.LoadingAnimation}
                      alt="Loading animation"
                      className={`size-5`}
                    />
                  ) : (
                    playButtonTitle
                  )
                }
                variant="primary"
                type="submit"
                disabled={eventVideoDetails?.videos?.length === 0}
                onClick={onPlayVideoClick}
                customWrapperClass={"min-w-20"}
              />
              <AdminFormFieldSubmit
                label={
                  downloadingVideo ? (
                    <img
                      src={svgIcons.LoadingAnimation}
                      alt="Loading animation"
                      className={`size-5`}
                    />
                  ) : (
                    "Download"
                  )
                }
                variant="primary"
                disabled={eventVideoDetails?.videos?.length === 0}
                type="submit"
                onClick={onDownloadEventVideoClick}
                customWrapperClass={"min-w-24"}
              />
            </div>
          )}
          {isSDCardDataSelected && (
            <>
              <div className="col-span-6 flex gap-1 items-center">
                <AdminFormFieldDropdown
                  id="duration-select"
                  label={t("video_length")}
                  name={"Select Time"}
                  placeholder={"Select Time"}
                  value={clipDuration.toString()}
                  onChange={(option) => {
                    if (option) {
                      setClipDuration(Number(option.value));
                      resetTask();
                    }
                  }}
                  customWrapperClass={"min-w-32 z-60"}
                  dimensionClass="h-11"
                  options={OPTION_VIDEO_CLIP}
                  key={`duration-select-${clipDuration}`}
                  menuPlacement="auto"
                />
                <AdminFormFieldDropdown
                  id="camera-select"
                  label={"Camera Type"}
                  name={"Select Camera"}
                  placeholder={"Select Camera"}
                  value={clipOptions.filter((opt) =>
                    selectedClipTypes.includes(opt.value)
                  )}
                  isMulti={true}
                  onChange={(selectedOptions: any) => {
                    if (selectedOptions) {
                      if (selectedOptions.length <= 4) {
                        const values = selectedOptions.map((o: any) => o.value);
                        setSelectedClipTypes(values);
                        resetTask();
                      } else {
                        toast.warning("You can select maximum of 4 views.");
                      }
                    } else {
                      setSelectedClipTypes([]);
                    }
                  }}
                  customWrapperClass={"min-w-32 z-60"}
                  dimensionClass="h-11"
                  options={clipOptions || []}
                  key={`camera-select-${clipType}`}
                  menuPlacement="auto"
                />
              </div>
              <div className="col-span-6 flex gap-1 items-center align-items-center">
                <AdminFormFieldSubmit
                  label={
                    playRequested ? (
                      <img
                        src={svgIcons.LoadingAnimation}
                        alt="Loading animation"
                        className={`size-5`}
                      />
                    ) : playbackState === VIDEO_PLAYBACK_STATE.playing ? (
                      "Pause"
                    ) : (
                      "Play"
                    )
                  }
                  variant="primary"
                  type="submit"
                  disabled={
                    !selectedTimelineData ||
                    selectedTimelineData?.length === 0 ||
                    selectedClipTypes.length === 0
                  }
                  onClick={onPlayVideoClick}
                  customWrapperClass={"min-w-20"}
                />
                <AdminFormFieldSubmit
                  label={
                    downloadingVideo ? (
                      <img
                        src={svgIcons.LoadingAnimation}
                        alt="Loading animation"
                        className={`size-5`}
                      />
                    ) : (
                      "Request Video"
                    )
                  }
                  variant="primary"
                  disabled={
                    !selectedTimelineData ||
                    selectedTimelineData?.length === 0 ||
                    downloadingVideo
                  }
                  type="submit"
                  onClick={onRequestStorageVideoClick}
                  customWrapperClass={"min-w-32"}
                />
              </div>
            </>
          )}
        </div>
      )}
      <div className="col-span-9 grid grid-cols-12 gap-2 mt-2">
        <div className="col-span-7 space-y-2 h-64">
          <div className="font-semibold text-base leading-none">
            {eventVideoDetails ? (
              <>{videoUrl ? t("video") : snapshotUrl ? "Photo" : ""}</>
            ) : (
              t("video")
            )}
          </div>
          {eventVideoDetails && (
            <>
              {!videoUrl && !snapshotUrl ? (
                <h1 className="font-medium text-md w-full h-full mt-40 align-middle text-center">
                  No Thumbnail available
                </h1>
              ) : videoUrl ? (
                <AppVideoElement
                  ref={videoRef}
                  controls
                  fixedHeightClass="h-[calc(75vh)]"
                  wrapperClassName="rounded-xl w-full h-64 object-cover"
                  onEnded={handleEnded}
                  onPlay={handlePlay}
                  onPause={handlePause}
                  playsInline
                  poster={snapshotUrl || undefined}
                  src={videoUrl}
                  videoAddress={
                    selectedEvent?.address || "Address not available"
                  }
                  stretchPosterImage={
                    !!eventVideoDetails.videos?.[0]?.capture_mode &&
                    [
                      CAMERA_CAPTURE_MODES.grid, // combined video from 4TVI cameras with GRID
                      CAMERA_CAPTURE_MODES.combined, // combined video from the front and in-cabin 2TVI cameras with S_F_I
                    ].includes(
                      `${eventVideoDetails.videos?.[0]?.capture_mode}` as CAMERA_CAPTURE_MODES
                    )
                      ? true // fill to stretch the poster to align with the video properly
                      : false
                  }
                  useVideoARForDimension={
                    !!eventVideoDetails.videos?.[0] &&
                    !!eventVideoDetails.videos?.[0]?.video_width &&
                    !!eventVideoDetails.videos?.[0]?.video_height
                      ? eventVideoDetails.videos?.[0]
                      : undefined
                  }
                />
              ) : snapshotUrl ? (
                <img
                  src={snapshotUrl}
                  className="rounded-xl w-full h-64 object-cover"
                  alt="Snapshot"
                />
              ) : null}
            </>
          )}
          {isSDCardDataSelected ? (
            <div className="rounded-xl w-full h-60 bg-black relative">
              <StorageVideoView
                cameraSerialNumber={serial_num}
                onPlayCallback={onStorageVideoPlayStart}
                onPauseCallback={onStorageVideoPaused}
                onStopCallback={onStorageVideoStopped}
                onTimeUpdate={onTimeUpdate}
                onNextCamera={selectedClipTypes.length > 1 ? onNextCamera : undefined}
                nextCameraLabel={nextCameraLabel}
              />
              {selectedClipTypes.length > 1 && modalsState.showModalVideoPlayback && (
                <button
                  onClick={onNextCamera}
                  className="absolute bottom-4 left-4 px-3 py-1.5 bg-white/70 hover:bg-white text-black text-sm font-medium rounded-full shadow z-10"
                >
                  Next: {nextCameraLabel}
                </button>
              )}
            </div>
          ) : null}
        </div>
        <div className="col-span-5 space-y-2">
          <div className="font-semibold text-base leading-none">
            {t("event_list")}
          </div>
          <div className="bg-white rounded-lg h-full">
            <div className="flex justify-between items-center font-semibold text-sm leading-3 tracking-normal p-4 text-accent-blue-dark bg-accent-blue-pale">
              <div>{t("event_type")}</div>
              <div>{t("time")}</div>
            </div>
            <div className="w-full h-48 overflow-y-auto space-y-2">
              {eventList?.length > 0 ? (
                eventList?.map((event, index) => (
                  <div
                    key={`${event?.ticket}-${index}`}
                    className={`flex justify-between items-center p-2 ${event?.recordid === selectedEvent?.recordid ? "bg-accent-blue-paleO66" : "hover:bg-accent-blue-paleO66 cursor-pointer"}`}
                    onClick={() => onEventSelect?.(event)}
                  >
                    <div>{event?.status?.description}</div>
                    <div>
                      {formattedDateTimeBasedOnTimezone?.(
                        dbTimeToJsTime(event.timestamp),
                        convertToTZForDateFnsNoHook(tzForDateFns),
                        APP_CONFIG.DATETIME_FORMATS.BACKEND.TIME
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <h1 className="font-medium text-md w-full mt-20 align-middle text-center">
                  No Events available
                </h1>
              )}
            </div>
          </div>
        </div>
      </div>
      <ModalDownloadConfirmation handleDownload={handleEventVideoDownload} />
    </>
  );
};

export default EventDetails;
